import { FillScreen } from "@/components/Layout";
import { defaultNS } from "@/lib/i18n";
import { LIST_EMPTY_DATA } from "@/lib/listPage";
import { createFileRoute, useLoaderData } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SoftwareAlertHelpText as Help } from "../../-components/apps-alerts-help-text";
import {
  SoftwareAlertTabs,
  useSoftwareAlertTabs,
} from "../../-components/apps-alerts-tabs";
import { SoftwareAlertType } from "../../-zod-schema";
import { SoftwareAlertsInactiveUserFilter as Filter } from "./-components/filter";
import { SoftwareAlertsInactiveUserTable as Table } from "./-components/table";
import { useExport, useSoftwareAlertsInactiveUser } from "./-query";
import { type SearchParams, SearchSchema } from "./-zod-schema";

export const Route = createFileRoute(
  "/_staff/software/_layout/alerts/_alerts/inactive-user/",
)({
  validateSearch: zodValidator(SearchSchema),
  component: SoftwareAlertsInactiveUser,
});

function SoftwareAlertsInactiveUser() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.software.alert",
  });

  // 共通系
  const counts = useLoaderData({ from: Route.parentRoute.id });
  const { onChangeTab, emptyCounts } = useSoftwareAlertTabs();

  // ここからInactive Userの話
  const { data, isPending } = useSoftwareAlertsInactiveUser(search);

  const { mutate: onExport, isPending: exporting } = useExport(search);

  const onSearch = useCallback(
    (query: Partial<SearchParams>) => {
      navigate({ search: (prev) => ({ ...prev, ...query }) });
    },
    [navigate],
  );

  const onChangeFilter = useCallback(
    // 検索条件を変更したら1ページ目から検索する
    (query: Partial<SearchParams>) => onSearch({ ...query, pageIndex: 0 }),
    [onSearch],
  );

  return (
    <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <SoftwareAlertTabs
        value={SoftwareAlertType.inactive.key}
        onChange={onChangeTab}
        counts={counts ?? emptyCounts}
      />
      <Help title={t("help.inactive.title")} text={t("help.inactive.text")} />
      <Filter
        exporting={exporting}
        onClickExport={onExport}
        state={search}
        onChange={onChangeFilter}
      />
      <Table
        data={data ?? LIST_EMPTY_DATA}
        search={search}
        onSearch={onSearch}
        isPending={isPending}
      />
    </FillScreen>
  );
}
